import AddIcon from "@mui/icons-material/Add";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { Autocomplete, Box, Checkbox, Chip, Divider, FormControlLabel, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import SysColumn from "app/class/sys-column";
import { useTables } from "app/hooks/useTables";
import { BaseField } from "./BaseField";
import { Controller, useFormContext } from "react-hook-form";
import { FieldRuntimeProps } from "modules/record/record-producer/types/record-producer-types";
import { useRecordProducer } from "../RecordProducerContext";
import { buildRequiredRule, getRequiredFieldMessage } from "../../services/recordProducerValidation";
import { resolvePermissionFieldProfile } from "./permission-array-field/resolvePermissionFieldProfile";
import { PermissionFieldProfile, PermissionOption } from "./permission-array-field/types";
import { safeParsePermissionArray, serializePermissionArray } from "./permission-array-field/permissionValue";
import { flussStartInstancePermission, FlussDefinitionOption, useFlussDefinitions } from "./permission-array-field/useFlussDefinitions";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFieldHelperText } from "../../services/fieldHelperText";

interface PermissionArrayFieldProps {
    column: SysColumn;
    runtimeProps?: FieldRuntimeProps;
}

const groupedOptions = (options: PermissionOption[]) => {
    return options.reduce<Record<string, PermissionOption[]>>((acc, option) => {
        acc[option.group] = [...(acc[option.group] || []), option];
        return acc;
    }, {});
};

const normalizeText = (value: string) => value.trim();

export const PermissionArrayInput = ({
    value,
    onChange,
    disabled,
    profile,
    error,
    helperText,
}: {
    value: unknown;
    onChange: (value: string) => void;
    disabled?: boolean;
    profile: PermissionFieldProfile;
    error?: boolean;
    helperText?: ReactNode;
}) => {
    const { t } = useTranslation();
    const { data: tables = [] } = useTables();
    const { data: flussDefinitions = [] } = useFlussDefinitions(profile.allowFlussDefinitions === true);
    const [tableName, setTableName] = useState<string | null>(null);
    const [flussDefinition, setFlussDefinition] = useState<FlussDefinitionOption | null>(null);
    const [host, setHost] = useState("");
    const [secret, setSecret] = useState("");
    const [custom, setCustom] = useState("");

    const selected = safeParsePermissionArray(value);
    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const tableOptions = useMemo(() => tables.map((table) => table.name).filter(Boolean).sort(), [tables]);
    const optionGroups = useMemo(() => groupedOptions(profile.options), [profile.options]);

    const commit = (next: string[]) => {
        onChange(serializePermissionArray(next.filter(Boolean)));
    };

    const toggle = (permission: string) => {
        if (disabled) return;
        commit(selectedSet.has(permission)
            ? selected.filter((item) => item !== permission)
            : [...selected, permission]);
    };

    const addPermission = (permission: string) => {
        const normalized = normalizeText(permission);
        if (!normalized || disabled || selectedSet.has(normalized)) return;
        commit([...selected, normalized]);
    };

    const removePermission = (permission: string) => {
        if (disabled) return;
        commit(selected.filter((item) => item !== permission));
    };

    const addTablePermission = (prefix: "db.read" | "db.write") => {
        if (!tableName) return;
        addPermission(`${prefix}:${tableName}`);
    };

    const addFlussPermission = () => {
        if (!flussDefinition) return;
        addPermission(flussStartInstancePermission(flussDefinition.definition_key));
        setFlussDefinition(null);
    };

    const addHostPermission = () => {
        addPermission(`http.call:${host}`);
        setHost("");
    };

    const addSecretPermission = (prefix: "secret.read" | "secret.write") => {
        addPermission(`${prefix}:${secret}`);
        setSecret("");
    };

    const addCustomPermission = () => {
        addPermission(custom);
        setCustom("");
    };

    return (
        <Box
            sx={{
                border: "1px solid",
                borderColor: error ? "error.main" : "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
                p: 1.5,
            }}
        >
            <Stack spacing={1.5}>
                {Object.entries(optionGroups).map(([group, options]) => (
                    <Box key={group}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                            {t(group)}
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 0.25 }}>
                            {options.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={selectedSet.has(option.value)}
                                            disabled={disabled}
                                            onChange={() => toggle(option.value)}
                                        />
                                    }
                                    label={<Typography variant="body2">{t(option.label)}</Typography>}
                                />
                            ))}
                        </Box>
                    </Box>
                ))}

                {profile.kind === "runtime_capabilities" && (
                    <>
                        <Divider />
                        <Box>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                                {t("Database")}
                            </Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                                <Autocomplete
                                    size="small"
                                    openText={`${t("Open")} ${t("Table")}`}
                                    clearText={`${t("Clear")} ${t("Table")}`}
                                    options={tableOptions}
                                    value={tableName}
                                    onChange={(_, nextValue) => setTableName(nextValue)}
                                    renderInput={(params) => <TextField {...params} label={t("Table")} />}
                                    disabled={disabled}
                                    sx={{ minWidth: 260, flex: 1 }}
                                />
                                <Stack direction="row" spacing={0.5}>
                                    <Tooltip title={t("Add read access")}>
                                        <span>
                                            <IconButton size="small" aria-label={String(t("Add read access"))} title={String(t("Add read access"))} disabled={disabled || !tableName} onClick={() => addTablePermission("db.read")}>
                                                <SearchIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={t("Add write access")}>
                                        <span>
                                            <IconButton size="small" aria-label={String(t("Add write access"))} title={String(t("Add write access"))} disabled={disabled || !tableName} onClick={() => addTablePermission("db.write")}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </Box>

                        {profile.allowFlussDefinitions === true && (
                            <Box>
                                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                                    {t("Fluss")}
                                </Typography>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                                    <Autocomplete
                                        size="small"
                                        openText={`${t("Open")} ${t("Fluss definition")}`}
                                        clearText={`${t("Clear")} ${t("Fluss definition")}`}
                                        options={flussDefinitions}
                                        value={flussDefinition}
                                        onChange={(_, nextValue) => setFlussDefinition(nextValue)}
                                        getOptionLabel={(option) => option.definition_key}
                                        isOptionEqualToValue={(option, current) => option.definition_key === current.definition_key}
                                        renderInput={(params) => <TextField {...params} label={t("Fluss definition")} />}
                                        disabled={disabled}
                                        sx={{ minWidth: 260, flex: 1 }}
                                    />
                                    <Tooltip title={t("Add Fluss start access")}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label={String(t("Add Fluss start access"))}
                                                title={String(t("Add Fluss start access"))}
                                                disabled={disabled || !flussDefinition}
                                                onClick={addFlussPermission}
                                            >
                                                <AccountTreeIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        )}

                        <Box>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                                {t("HTTP")}
                            </Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                                <TextField
                                    size="small"
                                    label={t("Host")}
                                    value={host}
                                    onChange={(event) => setHost(event.target.value)}
                                    disabled={disabled}
                                    sx={{ minWidth: 260, flex: 1 }}
                                />
                                <Tooltip title={t("Add HTTP access")}>
                                    <span>
                                        <IconButton size="small" aria-label={String(t("Add HTTP access"))} title={String(t("Add HTTP access"))} disabled={disabled || !normalizeText(host)} onClick={addHostPermission}>
                                            <PublicIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Box>

                        <Box>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                                {t("Secrets")}
                            </Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                                <TextField
                                    size="small"
                                    label={t("Key")}
                                    value={secret}
                                    onChange={(event) => setSecret(event.target.value)}
                                    disabled={disabled}
                                    sx={{ minWidth: 260, flex: 1 }}
                                />
                                <Stack direction="row" spacing={0.5}>
                                    <Tooltip title={t("Add secret read")}>
                                        <span>
                                            <IconButton size="small" aria-label={String(t("Add secret read"))} title={String(t("Add secret read"))} disabled={disabled || !normalizeText(secret)} onClick={() => addSecretPermission("secret.read")}>
                                                <VpnKeyIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={t("Add secret write")}>
                                        <span>
                                            <IconButton size="small" aria-label={String(t("Add secret write"))} title={String(t("Add secret write"))} disabled={disabled || !normalizeText(secret)} onClick={() => addSecretPermission("secret.write")}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </Box>
                    </>
                )}

                {profile.kind === "freeform_scope" && (
                    <>
                        <Divider />
                        <Box>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                                {t("Scope")}
                            </Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                                <TextField
                                    size="small"
                                    label={t("Value")}
                                    value={custom}
                                    onChange={(event) => setCustom(event.target.value)}
                                    disabled={disabled}
                                    sx={{ minWidth: 260, flex: 1 }}
                                />
                                <Tooltip title={t("Add scope")}>
                                    <span>
                                        <IconButton size="small" aria-label={String(t("Add scope"))} title={String(t("Add scope"))} disabled={disabled || !normalizeText(custom)} onClick={addCustomPermission}>
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Box>
                    </>
                )}

                {profile.kind !== "freeform_scope" && (
                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                            {t("Custom")}
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                            <TextField
                                size="small"
                                label={t("Token")}
                                value={custom}
                                onChange={(event) => setCustom(event.target.value)}
                                disabled={disabled}
                                sx={{ minWidth: 260, flex: 1 }}
                            />
                            <Tooltip title={t("Add custom token")}>
                                <span>
                                    <IconButton size="small" aria-label={String(t("Add custom token"))} title={String(t("Add custom token"))} disabled={disabled || !normalizeText(custom)} onClick={addCustomPermission}>
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Box>
                )}

                {selected.length > 0 && (
                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                            {t("Selected")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                            {selected.map((permission) => (
                                <Chip
                                    key={permission}
                                    size="small"
                                    label={permission}
                                    onDelete={disabled ? undefined : () => removePermission(permission)}
                                    deleteIcon={<DeleteOutlineIcon />}
                                    sx={{ maxWidth: "100%" }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {helperText && (
                    <Typography variant="caption" color={error ? "error" : "text.secondary"}>
                        {helperText}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

export const PermissionArrayField = ({ column, runtimeProps }: PermissionArrayFieldProps) => {
    const { control } = useFormContext();
    const recordProducer = useRecordProducer();
    const profile = resolvePermissionFieldProfile({ table: recordProducer.table, column });

    if (!profile) {
        return null;
    }

    return (
        <BaseField column={column} required={runtimeProps?.required} hidden={runtimeProps?.hidden} as="group">
            <Controller
                name={column.name}
                control={control}
                defaultValue={column.default_value || "[]"}
                rules={{ required: buildRequiredRule(runtimeProps?.required ?? column.is_mandatory, getRequiredFieldMessage(column)) }}
                render={({ field, fieldState }) => (
                    <PermissionArrayInput
                        value={field.value}
                        onChange={field.onChange}
                        disabled={runtimeProps?.readonly}
                        profile={profile}
                        error={!!fieldState.error}
                        helperText={getFieldHelperText(fieldState, runtimeProps)}
                    />
                )}
            />
        </BaseField>
    );
};
